import { Card, CardActions, CardContent, CircularProgress, Theme } from '@mui/material';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import Text from '../text';
import Tooltip from '../tooltip';
import Icon from '../icon';
import { ReactNode } from 'react';

interface Props {
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  title?: string;
  description?: string;
  descriptionTooltip?: string;
  loading?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  topBorder: {
    borderTop: `1px solid ${theme.palette.surface.disabled.main}`,
  },
  bottomBorder: {
    borderBottom: `1px solid ${theme.palette.surface.disabled.main}`,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    gap: 8,
  },
  title: {
    fontWeight: `700 !important`,
  },
}));

const DashboardCard = ({
  children,
  footer,
  className,
  title,
  description,
  descriptionTooltip,
  loading,
}: Props) => {
  const hasHeader = !!title || !!description;
  const hasDescriptionTooltip = !!descriptionTooltip && !!description;
  const hasFooter = !!footer;
  const styles = useStyles();

  const cardCompClass = classNames(styles.card, className);
  const contentCompClass = classNames(
    hasHeader && styles.topBorder,
    hasFooter && styles.bottomBorder
  );

  return (
    <Card elevation={0} className={cardCompClass}>
      {title && (
        <Text bold className={styles.title}>
          {title}
        </Text>
      )}
      {description && (
        <Text size="small" color="grey">
          {description}
          {hasDescriptionTooltip && (
            <Tooltip title={descriptionTooltip}>
              <Icon name="info-circle" size="small" color="grey" />
            </Tooltip>
          )}
        </Text>
      )}
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <CardContent className={contentCompClass}>{children}</CardContent>
          <CardActions>{footer}</CardActions>
        </>
      )}
    </Card>
  );
};

export default DashboardCard;
